import { useCallback, useEffect, useState } from 'react'
import { ApiError, api, type Account, type Payment, type Plan, type SubscriptionView } from '../api'
import { until } from '../format'
import { confirmAction, haptic, payInvoice } from '../telegram'

const LICENSE_TEXT: Record<Account['license']['state'], string> = {
  Active: 'Активна',
  Expired: 'Истекла',
  Revoked: 'Отозвана',
  None: 'Нет подписки',
}

/**
 * The licence, what can be bought, the auto-renewal and the payments.
 *
 * Paying happens in Telegram's own invoice sheet (openInvoice). What it reports back is only a hint: the
 * licence moves when the bot receives Telegram's successful_payment, so after "paid" this card watches
 * the account for a few seconds instead of believing the sheet.
 */
export function SubscriptionCard({
  license,
  onChanged,
  onShowTerms,
}: {
  license: Account['license']
  /** Reloads the account; resolves with it, so a purchase can be seen to land. */
  onChanged: () => Promise<Account | null>
  onShowTerms: () => void
}) {
  const [view, setView] = useState<SubscriptionView | null>(null)
  const [payments, setPayments] = useState<Payment[] | null>(null)
  const [showPayments, setShowPayments] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setView(await api.getSubscription())
    } catch {
      setView(null)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function buy(plan: Plan) {
    setBusy(plan.id)
    setError(null)
    setNotice(null)
    try {
      const { invoiceLink } = await api.createInvoice(plan.id)
      const before = `${license.state}|${license.expiresAtUtc}`
      const status = await payInvoice(invoiceLink)
      if (status === 'cancelled') return
      if (status === 'failed') {
        haptic('error')
        setError('Оплата не прошла. Звёзды не списаны — попробуйте ещё раз.')
        return
      }

      haptic('ok')
      setNotice(
        status === 'paid'
          ? 'Оплата принята — подписка обновится через несколько секунд.'
          : 'Оплата обрабатывается — подписка обновится, как только Telegram её подтвердит.',
      )
      // The bot credits the payment when Telegram tells it; watch for that rather than trust the sheet.
      for (let i = 0; i < 15; i++) {
        await new Promise((r) => setTimeout(r, 2000))
        const fresh = await onChanged()
        if (fresh && `${fresh.license.state}|${fresh.license.expiresAtUtc}` !== before) {
          setNotice('Подписка продлена. Спасибо!')
          break
        }
      }
      await load()
      if (showPayments) setPayments(await api.getPayments())
    } catch (e) {
      haptic('error')
      setError(messageFor(e))
    } finally {
      setBusy(null)
    }
  }

  async function cancelRenewal() {
    const ok = await confirmAction('Отключить автопродление? Подписка продолжит действовать до конца оплаченного срока.')
    if (!ok) return
    setBusy('cancel')
    setError(null)
    setNotice(null)
    try {
      const result = await api.cancelRenewal()
      if (result.live === 0) setNotice('Автопродления нет — отключать нечего.')
      else if (result.failed > 0)
        setError('Отключить автопродление удалось не полностью. Отключите его в Telegram: Настройки → Мои звёзды.')
      else {
        const left = until(result.activeUntilUtc)
        setNotice(`Автопродление отключено.${left ? ` Подписка действует ещё ${left}.` : ''}`)
      }
      haptic('ok')
      await load()
    } catch (e) {
      haptic('error')
      setError(messageFor(e))
    } finally {
      setBusy(null)
    }
  }

  async function togglePayments() {
    const next = !showPayments
    setShowPayments(next)
    if (next && payments === null) {
      try {
        setPayments(await api.getPayments())
      } catch {
        setPayments([])
      }
    }
  }

  const licenseLeft = until(license.expiresAtUtc)

  return (
    <section className="card">
      <div className="row">
        <span className={`dot ${license.isActive ? 'ok' : 'bad'}`} />
        <div className="grow">
          <div className="title">
            Подписка: {LICENSE_TEXT[license.state] ?? license.state}
            {license.state === 'Active' && license.tier === 'Trial' && ' (пробная)'}
          </div>
          {licenseLeft && license.isActive && <div className="muted">Осталось {licenseLeft}</div>}
        </div>
      </div>

      {error && <p className="banner bad">{error}</p>}
      {notice && <p className="banner">{notice}</p>}

      {view?.autoRenew && (
        <div className="row">
          <div className="grow">
            <div className="small">
              Автопродление включено
              {view.autoRenew.nextChargeAtUtc && until(view.autoRenew.nextChargeAtUtc)
                ? ` — следующее списание через ${until(view.autoRenew.nextChargeAtUtc, 'accusative')}`
                : ''}
            </div>
          </div>
          <button className="chip" onClick={() => void cancelRenewal()} disabled={busy !== null}>
            Отключить
          </button>
        </div>
      )}

      {view && !view.enabled && <p className="muted note">Продажа подписок сейчас закрыта.</p>}
      {view && view.enabled && !view.available && (
        <p className="muted note">Оплата на сервере сейчас не настроена — напишите в поддержку.</p>
      )}

      {view?.enabled && view.available && view.plans.length > 0 && (
        <>
          <div className="plans">
            {view.plans.map((plan) => (
              <button
                key={plan.id}
                className="button primary plan"
                onClick={() => void buy(plan)}
                disabled={busy !== null}
                title={plan.description}
              >
                <span>{busy === plan.id ? 'Открываем счёт…' : plan.name}</span>
                <span className="price">
                  {plan.priceStars} ★{plan.recurring ? ' / 30 дней' : ''}
                </span>
              </button>
            ))}
          </div>
          <p className="muted small">
            Оплата звёздами Telegram. Срок добавляется к текущему, оплата заранее не сгорает. Оплачивая, вы принимаете{' '}
            <button className="link" onClick={onShowTerms}>
              условия подписки
            </button>
            .
          </p>
        </>
      )}

      <button className="link small" onClick={() => void togglePayments()}>
        {showPayments ? 'Скрыть платежи' : 'История платежей'}
      </button>
      {showPayments && payments !== null && (
        <div className="payments">
          {payments.length === 0 && <p className="muted small">Платежей пока не было.</p>}
          {payments.map((p) => (
            <div className="payment small" key={p.chargeId}>
              <div>
                {new Date(p.paidAtUtc).toLocaleDateString('ru-RU')} · {p.planName} · {p.amountStars} ★
                {p.status === 'refunded' && ' · возвращён'}
              </div>
              {/* The code support asks for; selectable so it can be copied. */}
              <div className="muted code">{p.chargeId}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function messageFor(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.isOffline) return 'Нет связи с сервером — попробуйте ещё раз.'
    if (e.message && e.message !== e.code) return e.message
    if (e.isServerRestarting) return 'Сервер перезапускается, попробуйте через минуту.'
  }
  return 'Не получилось — попробуйте ещё раз.'
}
